import { backToPlanning, leaveProductionQueue } from '../api.ts';
import * as wording from '../wording.ts';
import { type ActionDialog, type ActionHost, approveVersionOf, settle } from './actions.ts';
import type { BoardCard, BoardColumn } from './model.ts';

/**
 * What dropping a card on another column means (US-025, Design reference
 * § Moving a card). Pure, like `actions.ts`: a move runs against the same
 * {@link ActionHost} as a primary action, so a drop, the Move menu (US-026)
 * and the keyboard share one table.
 */
export type Move =
  /** Its own column: only a reorder (§ Manual order, US-026), never a move. */
  | { readonly kind: 'same' }
  | { readonly kind: 'refused'; readonly reason: string }
  | {
      readonly kind: 'action';
      /** The dialog the move opens, if it opens one; it then finishes there. */
      readonly dialog: ActionDialog | null;
      /** Does it; a refusal is thrown, as a primary action's is. */
      readonly run: (host: ActionHost) => Promise<void>;
    };

/** The move from `card`'s column to `to`; the table's rows in order, the first that matches wins. */
export function transition(card: BoardCard, to: BoardColumn): Move {
  const from = card.column;
  if (to === from) return { kind: 'same' };

  if (card.type === 'idea') {
    if (to !== 'planning') return refused(wording.MOVE_IDEA_TO_PLANNING_FIRST);
    switch (card.idea.status) {
      case 'drafted':
        return opens('approve-idea', card);
      case 'failed':
        return refused(wording.MOVE_IDEA_FAILED);
      default:
        return refused(wording.MOVE_IDEA_NOT_WORKED_OUT);
    }
  }

  if (to === 'ideas') return refused(wording.MOVE_NOT_AN_IDEA_AGAIN);
  const { content } = card;

  if (from === 'planning' && to === 'production') {
    return content.planParses ? opens('mark-ready', card) : refused(wording.MOVE_NO_FINISHED_PLAN);
  }
  if (from === 'production' && to === 'planning') {
    if (content.status !== 'ready') return refused(wording.MOVE_STOP_PRODUCTION_FIRST);
    const queued = content.queuePosition !== null;
    return calls((host) =>
      settle(host, async () => {
        if (queued) await leaveProductionQueue(content.id);
        await backToPlanning(content.id);
        return { text: wording.boardBackToPlanning(content.name) };
      }),
    );
  }
  if (from === 'review' && to === 'to-post') {
    return calls((host) => settle(host, () => approveVersionOf(content.id, content.name)));
  }
  if (to === 'production' && from !== 'planning') return opens('request-changes', card);
  if (from === 'to-post' && to === 'scheduled') return opens('schedule', card);
  if ((from === 'to-post' || from === 'scheduled') && to === 'posted') return opens('mark-posted', card);
  if (from === 'scheduled' && to === 'to-post') return opens('unschedule', card);
  if (from === 'posted' && to === 'to-post') return opens('undo-posting', card);
  if (from === 'posted' && to === 'scheduled') return refused(wording.MOVE_UNDO_POSTING_FIRST);
  if (from === 'review' && (to === 'scheduled' || to === 'posted')) return refused(wording.MOVE_APPROVE_FIRST);
  if (from === 'planning' || from === 'production') return refused(wording.MOVE_PRODUCTION_HAS_TO_FINISH);
  if (to === 'review') return refused(wording.MOVE_REVIEW_AFTER_PRODUCTION);
  // Not in the table: a made version back to Planning. Changing the plan of
  // something already made goes through Request changes.
  return refused(wording.MOVE_BACK_TO_PLANNING);
}

/** Why a lane change is refused, by grouping; `null` where a lane change is a move (campaign). */
export const LANE_CHANGE_REFUSED: Readonly<Record<'campaign' | 'book' | 'funnel', string | null>> = {
  campaign: null,
  book: wording.MOVE_LANE_BOOK,
  funnel: wording.MOVE_LANE_FUNNEL,
};

function refused(reason: string): Move {
  return { kind: 'refused', reason };
}

function opens(dialog: ActionDialog, card: BoardCard): Move {
  return {
    kind: 'action',
    dialog,
    run: (host) => {
      host.openDialog(dialog, card);
      return Promise.resolve();
    },
  };
}

function calls(run: (host: ActionHost) => Promise<void>): Move {
  return { kind: 'action', dialog: null, run };
}

/** The drag data a card carries: its card id. */
export const CARD_DATA_TYPE = 'application/x-mc-card';
